import React, { FunctionComponent, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";

import SignIn from "./auth/SignIn";
import Registrations from "./registration/Registrations";
import Waitlist from "./waitlist/Waitlist";
import UpdateRegistrationEditor from "./registration/UpdateRegistrationEditor";
import Limits from "./limit/Limits";
import Sidebar from "./common/sidebar/Sidebar";
import { MobileBar } from "./common/sidebar/MobileBar";
import UpdateLimitEditor from "./limit/UpdateLimitEditor";
import Dashboard from "./dashboard/Dashboard";
import Admin from "./admin/Admin";
import { Settings } from "./settings/Settings";
import CreateRegistrationEditor from "./registration/CreateRegistrationEditor";
import CreateLimitEditor from "./limit/CreateLimitEditor";
import { useAuthUser } from "../hooks/useAuthUser";
import { firebaseAppAuth } from "../lib/firebase";
import { useAuthAdmin } from "../hooks/useAuthAdmin";
import { GlobalErrorBoundary } from "../core/GlobalErrorBoundary";
import SignUp from "./auth/SignUp";
import { Form } from "./form/Form";
import { Email } from "./email/Email";
import { SpielgruppePlus } from "./premium/SpielgruppePlus";
import { Footer } from "./common/footer/Footer";
import { Datenschutz } from "./staticsites/Datenschutz";
import { Agb } from "./staticsites/Agb";
import { PublicRegistrationForm } from "./registrationform/PublicRegistrationForm";
import ResetPassword from "./auth/ResetPassword";
import { identifyUser, resetUser } from "../helper/analytics";
import { useUserContext } from "../contexts/UserContext";
import UserManagement from "./admin/UserManagement";
import { usePageTracking } from "../hooks/usePageTracking";
import { toastIcon } from "./common/toastIcon";

const SIDEBAR_COLLAPSED_KEY = "webtie.sidebarCollapsed";

const App: FunctionComponent = () => {
  const { user, loading: authLoading } = useAuthUser();
  const { activeUser } = useUserContext();
  const isAuthenticated = !!user;
  const isAdmin = useAuthAdmin(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
  );

  usePageTracking();

  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const toggleSidebar = (): void => {
    setSidebarCollapsed((collapsed) => {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!collapsed));
      return !collapsed;
    });
  };

  useEffect(() => {
    if (user && activeUser) {
      identifyUser(user.uid, {
        email: user.email,
        displayName: user.displayName,
        isAdmin,
        userId: activeUser.id,
        domain: import.meta.env.VITE_PUBLIC_DOMAIN
      });
    }
  }, [user, activeUser, isAdmin]);

  if (authLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  // Handle user logout
  const handleSignOut = async (): Promise<void> => {
    resetUser();
    return firebaseAppAuth.signOut();
  };

  const appRoutes = (
    <Routes>
      <Route path="/user/:userId/dashboard" element={<Dashboard isAuthenticated={isAuthenticated} />} />
      <Route
        path="/user/:userId/registrations/create"
        element={<CreateRegistrationEditor isAuthenticated={isAuthenticated} />}
      />
      <Route
        path="/user/:userId/registrations/:registrationId"
        element={<UpdateRegistrationEditor isAuthenticated={isAuthenticated} />}
      />
      <Route path="/user/:userId/limits/create" element={<CreateLimitEditor isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/limits/:limitId" element={<UpdateLimitEditor isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/limits" element={<Limits isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/form" element={<Form isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/email" element={<Email isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/registrations" element={<Registrations isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/waitlist" element={<Waitlist isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/admin" element={<Admin isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/user-management" element={<UserManagement isAuthenticated={isAuthenticated} />} />
      <Route path="/user/:userId/settings" element={<Settings isAuthenticated={isAuthenticated} isAdmin={isAdmin} />} />
      <Route path="/user/:userId/plus" element={<SpielgruppePlus isAuthenticated={isAuthenticated} />} />
      <Route path="/signup" element={<SignUp user={user} />} />
      <Route path="/reset-password" element={<ResetPassword user={user} />} />
      <Route path="/formular/:userId" element={<PublicRegistrationForm />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<Agb />} />
      <Route path="/" element={<SignIn user={user} />} />
    </Routes>
  );

  return (
    <div className={"h-100"}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Webtie Anmeldungen</title>
        <meta name="description" content="Anmeldungen online verwalten" />
      </Helmet>
      <GlobalErrorBoundary>
        <>
          <Router>
            {user ? (
              <div className="app-shell">
                <Sidebar
                  userId={activeUser?.id || user.uid}
                  isAdmin={isAdmin}
                  collapsed={sidebarCollapsed}
                  onToggleCollapsed={toggleSidebar}
                  mobileOpen={mobileNavOpen}
                  onCloseMobile={() => setMobileNavOpen(false)}
                  signOut={() => handleSignOut()}
                />
                {mobileNavOpen && <div className="app-sidebar-backdrop" onClick={() => setMobileNavOpen(false)} />}
                <div className="app-main">
                  <MobileBar userId={activeUser?.id || user.uid} onOpenMenu={() => setMobileNavOpen(true)} />
                  <div className="app-scroll">{appRoutes}</div>
                  <Footer userId={activeUser?.id || user.uid} />
                </div>
              </div>
            ) : (
              <div className="app-shell">
                <div className="app-main">
                  <div className="app-scroll">{appRoutes}</div>
                  <Footer />
                </div>
              </div>
            )}
          </Router>
        </>
      </GlobalErrorBoundary>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        draggable
        theme="light"
        icon={toastIcon}
      />
    </div>
  );
};

export default App;
